/** Error from the API with status and response body */
export class ApiError extends Error {
  readonly status: number;
  readonly statusText: string;
  readonly body: unknown;

  constructor(status: number, statusText: string, body: unknown) {
    const detail = extractErrorDetail(body);
    const message = detail ?? `API error ${status}: ${statusText}`;
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.statusText = statusText;
    this.body = body;
  }
}

function extractErrorDetail(body: unknown): string | null {
  if (!body || typeof body !== 'object') return null;

  const candidate = body as Record<string, unknown>;

  if (typeof candidate.detail === 'string' && candidate.detail.trim()) {
    return candidate.detail;
  }

  if (typeof candidate.title === 'string' && candidate.title.trim()) {
    return candidate.title;
  }

  if (typeof candidate.error === 'string' && candidate.error.trim()) {
    return candidate.error;
  }

  if (typeof candidate.message === 'string' && candidate.message.trim()) {
    return candidate.message;
  }

  return null;
}

export type ParamValue = string | string[] | undefined;

/** Build a URL from base, path, and query parameters. Array values repeat the key. */
export function buildUrl(
  baseUrl: string,
  path: string,
  params?: Record<string, ParamValue>,
): string {
  const fullPath = `${baseUrl}${path}`;
  if (!params) return fullPath;

  const parts: string[] = [];
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined) continue;
    if (Array.isArray(value)) {
      for (const v of value) {
        parts.push(`${encodeURIComponent(key)}=${encodeURIComponent(v)}`);
      }
    } else {
      parts.push(`${encodeURIComponent(key)}=${encodeURIComponent(value)}`);
    }
  }

  return parts.length > 0 ? `${fullPath}?${parts.join('&')}` : fullPath;
}

export interface RequestOptions {
  signal?: AbortSignal;
  timeout?: number;
  headers?: Record<string, string>;
}

const DEFAULT_TIMEOUT = 30_000;

/** Fetch JSON from a URL with timeout and cancellation support */
export async function fetchJson<T>(
  url: string,
  options?: RequestOptions,
  timeout: number = DEFAULT_TIMEOUT,
): Promise<T> {
  const effectiveTimeout = options?.timeout ?? timeout;
  const controller = new AbortController();

  if (options?.signal?.aborted) {
    throw options.signal.reason ?? new DOMException('Aborted', 'AbortError');
  }

  let timeoutId: ReturnType<typeof setTimeout> | undefined;
  if (effectiveTimeout > 0) {
    timeoutId = setTimeout(
      () =>
        controller.abort(new DOMException('Request timed out', 'TimeoutError')),
      effectiveTimeout,
    );
  }

  const onCallerAbort = (): void => controller.abort(options?.signal?.reason);
  options?.signal?.addEventListener('abort', onCallerAbort);

  try {
    const response = await fetch(url, {
      signal: controller.signal,
      headers: {
        Accept: 'application/hal+json, application/json',
        ...options?.headers,
      },
    });

    if (!response.ok) {
      let body: unknown;
      try {
        body = await response.json();
      } catch {
        body = null;
      }
      throw new ApiError(response.status, response.statusText, body);
    }

    return (await response.json()) as T;
  } finally {
    if (timeoutId !== undefined) clearTimeout(timeoutId);
    options?.signal?.removeEventListener('abort', onCallerAbort);
  }
}
